import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, FileText, RefreshCw, Truck, CheckCircle, Clock } from 'lucide-react';
import { API } from '../App';
import Header from '../components/Header';
import { toast } from 'sonner';

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const [orderRes, invoiceRes] = await Promise.all([
        axios.get(`${API}/orders/${orderId}`, { withCredentials: true }),
        axios.get(`${API}/orders/${orderId}/invoice`, { withCredentials: true })
      ]);
      setOrder(orderRes.data);
      setInvoice(invoiceRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/account');
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async () => {
    try {
      const response = await axios.post(`${API}/orders/${orderId}/reorder`, {}, { withCredentials: true });
      // Store cart items in localStorage
      const cartItems = response.data.cart_items.map(item => ({
        product_id: item.product_id || item.name,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image_url: ''
      }));
      localStorage.setItem('tagora_cart', JSON.stringify(cartItems));
      toast.success('Articles ajoutés au panier');
      navigate('/shop');
    } catch (error) {
      toast.error('Erreur lors de la recomande');
    }
  };

  const printInvoice = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <Clock className="w-5 h-5" />;
      case 'shipped': return <Truck className="w-5 h-5" />;
      case 'delivered': return <CheckCircle className="w-5 h-5" />;
      default: return <Package className="w-5 h-5" />;
    }
  };

  const getStatusSteps = () => {
    const steps = [
      { key: 'confirmed', label: 'Confirmée' },
      { key: 'shipped', label: 'Expédiée' },
      { key: 'delivered', label: 'Livrée' }
    ];
    const currentIndex = steps.findIndex(s => s.key === order?.status);
    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      current: index === currentIndex
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="px-6 sm:px-8 lg:px-12 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <Link
            to="/account"
            className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à mon compte
          </Link>

          {/* Order header */}
          <div className="card-brutal mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-charcoal/60 mb-1">COMMANDE</p>
                <h1 className="text-2xl font-bold text-charcoal font-heading">
                  #{order?.order_id?.replace('order_', '').toUpperCase()}
                </h1>
                <p className="text-charcoal/70 mt-1">{formatDate(order?.created_at)}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleReorder}
                  data-testid="reorder-btn"
                  className="btn-secondary text-sm px-4 py-2 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" /> Recommander
                </button>
                <button
                  onClick={printInvoice}
                  data-testid="invoice-btn"
                  className="btn-outline text-sm px-4 py-2 flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" /> Facture
                </button>
              </div>
            </div>
          </div>

          {/* Status tracker */}
          <div className="card-brutal mb-6">
            <h2 className="font-bold text-charcoal font-heading mb-6">Suivi de commande</h2>
            <div className="flex justify-between relative">
              <div className="absolute top-5 left-0 right-0 h-1 bg-charcoal/10">
                <div 
                  className="h-full bg-primary transition-all"
                  style={{ 
                    width: `${(getStatusSteps().filter(s => s.completed).length - 1) / (getStatusSteps().length - 1) * 100}%` 
                  }}
                ></div>
              </div>
              {getStatusSteps().map((step, index) => (
                <div key={step.key} className="relative flex flex-col items-center z-10">
                  <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                    step.completed 
                      ? 'bg-primary border-charcoal text-charcoal' 
                      : 'bg-white border-charcoal/20 text-charcoal/40'
                  }`}>
                    {getStatusIcon(step.key)}
                  </div>
                  <span className={`mt-2 text-sm font-bold ${step.completed ? 'text-charcoal' : 'text-charcoal/40'}`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Order items */}
          <div className="card-brutal mb-6">
            <h2 className="font-bold text-charcoal font-heading mb-4">Articles commandés</h2>
            <div className="space-y-4">
              {order?.items?.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-3 border-b border-charcoal/10 last:border-0">
                  <div>
                    <p className="font-bold text-charcoal">{item.name}</p>
                    <p className="text-sm text-charcoal/60">Quantité: {item.quantity}</p>
                  </div>
                  <p className="font-bold text-charcoal">{(item.price * item.quantity).toFixed(2)}€</p>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t-2 border-charcoal flex justify-between items-center">
              <span className="font-bold text-charcoal text-lg">Total</span>
              <span className="font-bold text-charcoal text-xl">{order?.total?.toFixed(2)}€</span>
            </div>
          </div>

          {/* Invoice preview (printable) */}
          <div className="card-brutal print:shadow-none print:border-0" id="invoice-section">
            <div className="flex justify-between items-start mb-8">
              <div>
                <h2 className="text-2xl font-bold text-charcoal font-heading">FACTURE</h2>
                <p className="text-charcoal/70">{invoice?.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-charcoal">{invoice?.company?.name}</p>
                <p className="text-sm text-charcoal/70">{invoice?.company?.address}</p>
                <p className="text-sm text-charcoal/70">{invoice?.company?.city}</p>
                <p className="text-sm text-charcoal/70">SIRET: {invoice?.company?.siret}</p>
              </div>
            </div>

            <div className="mb-8">
              <p className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal/60 mb-2">Facturé à</p>
              <p className="font-bold text-charcoal">{invoice?.user?.name}</p>
              <p className="text-charcoal/70">{invoice?.user?.email}</p>
              {invoice?.billing_address && (
                <>
                  <p className="text-charcoal/70">{invoice?.billing_address?.street}</p>
                  <p className="text-charcoal/70">{invoice?.billing_address?.postal_code} {invoice?.billing_address?.city}</p>
                </>
              )}
            </div>

            <table className="w-full mb-8">
              <thead>
                <tr className="border-b-2 border-charcoal">
                  <th className="text-left py-2 text-charcoal">Article</th>
                  <th className="text-right py-2 text-charcoal">Qté</th>
                  <th className="text-right py-2 text-charcoal">Prix unit.</th>
                  <th className="text-right py-2 text-charcoal">Total</th>
                </tr>
              </thead>
              <tbody>
                {order?.items?.map((item, index) => (
                  <tr key={index} className="border-b border-charcoal/10">
                    <td className="py-3 text-charcoal">{item.name}</td>
                    <td className="py-3 text-right text-charcoal">{item.quantity}</td>
                    <td className="py-3 text-right text-charcoal">{item.price?.toFixed(2)}€</td>
                    <td className="py-3 text-right text-charcoal">{(item.price * item.quantity).toFixed(2)}€</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-charcoal">
                  <td colSpan="3" className="py-3 font-bold text-charcoal text-right">Total TTC</td>
                  <td className="py-3 font-bold text-charcoal text-right text-lg">{order?.total?.toFixed(2)}€</td>
                </tr>
              </tfoot>
            </table>

            <p className="text-xs text-charcoal/50 text-center">
              TVA non applicable, article 293B du CGI
            </p>
          </div>
        </div>
      </main>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #invoice-section, #invoice-section * { visibility: visible; }
          #invoice-section { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default OrderDetails;
